/**
 * 교육 수강 신청 모달 Vue.js 통합 스크립트
 * 기존 JB 모달 패턴과 Vue.js 컴포넌트를 연동
 */

import Vue from 'vue';
import EduApplicationModal from './EduApplicationModal.vue';

// 전역 Vue 앱 인스턴스 저장
let eduModalApp = null;

/**
 * 모달 DOM 엘리먼트 생성
 *
 * @returns {HTMLElement} 생성된 모달 엘리먼트
 */
function createModalElement() {
    const modalElement = document.createElement('div');
    modalElement.id = 'eduApplicationModal';
    modalElement.className = 'edu-application-modal-wrapper';
    modalElement.style.display = 'none';
    modalElement.setAttribute('aria-hidden', 'true');
    modalElement.setAttribute('aria-modal', 'true');
    modalElement.setAttribute('role', 'dialog');
    modalElement.setAttribute('aria-labelledby', 'edu-modal-title');

    return modalElement;
}

/**
 * 교육 수강 신청 모달 표시 함수
 * 기존 JavaScript에서 호출하는 진입점
 *
 * @param {number|string} lectureId - 강좌 ID
 * @param {string} lectureType - 강좌 타입 (FREE, FREE_CONFIRM, CHARGED, CHARGED_CONFIRM)
 * @param {function} completedCallback - 신청 완료 콜백 함수
 */
function showEduApplicationModal(lectureId, lectureType = 'FREE', completedCallback = null) {
    // 로그인 상태 확인 추가
    if (!window.loginMember) {
        alert('로그인 후 이용 가능합니다.');
        return;
    }

    try {
        // 기존 모달이 열려있다면 정리
        if (eduModalApp) {
            eduModalApp.$destroy();
            eduModalApp = null;
        }

        // 모달 열기 전 현재 포커스 저장 (닫기 후 복귀용)
        showEduApplicationModal._lastFocusedElement = document.activeElement;

        // 모달 엘리먼트 생성 또는 가져오기
        let modalElement = document.getElementById('eduApplicationModal');
        if (!modalElement) {
            modalElement = createModalElement();
            document.body.appendChild(modalElement);
        }

        // Vue 2.x 인스턴스 생성 및 마운트
        eduModalApp = new Vue({
            el: '#eduApplicationModal',
            components: {
                'edu-application-modal': EduApplicationModal,
            },
            data: {
                lectureId,
                lectureType,
                modalId: 'eduApplicationModal',
                orgList: window.orgListData || [],
                lectureInfo: window.lectureInfo || {
                    isCampus: false,
                    isFree: false
                },
            },
            methods: {
                handleApplicationCompleted(result) {
                    if (typeof completedCallback === 'function') {
                        completedCallback(result);
                    }
                }
            },
            template: '<edu-application-modal :lecture-id="lectureId" :lecture-type="lectureType" :modal-id="modalId" :org-list="orgList" :lecture-info="lectureInfo" @application-completed="handleApplicationCompleted"></edu-application-modal>',
        });

        // 모달 표시
        modalElement.style.display = 'flex';
        document.body.classList.add('modal-open');

        // 접근성: 포커스 관리
        modalElement.setAttribute('aria-hidden', 'false');

        // Vue 마운트 후 포커스 이동 (setModal과 동일한 패턴 적용)
        // ※ Vue 2는 el 요소를 새 요소로 교체하므로 modalElement 변수가 분리된 구 요소를 가리킴
        //    → setTimeout 안에서 document.getElementById로 재조회 필요
        setTimeout(() => {
            requestAnimationFrame(() => {
                const currentModal = document.getElementById('eduApplicationModal');
                if (!currentModal) return;
                const titleEl = currentModal.querySelector('h2.title[tabindex="-1"]');
                const focusable = titleEl || currentModal.querySelector('button:not([disabled]):not(.close-modal)');
                if (focusable) {
                    focusable.focus();
                }
            });
        }, 300);
    } catch (error) {
        alert('모달을 열 수 없습니다. 페이지를 새로고침 후 다시 시도해 주세요.');
    }
}

/**
 * 교육 수강 신청 모달 닫기 함수
 *
 * @param {Event} event - 이벤트 객체 (선택사항)
 */
function closeEduApplicationModal(event) {
    if (event) {
        event.preventDefault();
    }

    const modalElement = document.getElementById('eduApplicationModal');

    if (modalElement) {
        // 모달 숨기기
        modalElement.style.display = 'none';
        modalElement.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('modal-open');
    }

    // Vue 인스턴스 제거
    if (eduModalApp) {
        eduModalApp.$destroy();
        eduModalApp = null;
    }

    // 모달 닫기 후 원래 포커스 복귀
    const lastFocused = showEduApplicationModal._lastFocusedElement;
    if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
        showEduApplicationModal._lastFocusedElement = null;
    }
}

/**
 * 기존 openTargetModal 함수와의 통합
 *
 * @param {Event} event - 클릭 이벤트
 * @param {string} modalType - 모달 타입 ("eduApplicationModal")
 * @param {Object} options - 추가 옵션
 */
function openEduTargetModal(event, modalType, options = {}) {
    if (modalType === 'eduApplicationModal') {
        const lectureId = options.lectureId || event.target.dataset.lectureId ||
            event.target.getAttribute('data-lecture-id');
        const lectureType = options.lectureType || event.target.dataset.lectureType ||
            event.target.getAttribute('data-lecture-type') || 'FREE';

        if (!lectureId) {
            alert('강좌 정보를 불러올 수 없습니다.');
            return;
        }

        showEduApplicationModal(lectureId, lectureType, options.completedCallback);
    }
}

/**
 * 교육 상세 페이지의 수강신청 버튼 클릭 핸들러
 * HTML: <button onclick="handleEduApplicationClick(this)" data-lecture-id="123">수강신청</button>
 *
 * @param {HTMLElement} element - 클릭된 버튼 엘리먼트
 */
function handleEduApplicationClick(element) {
    const lectureId = element.dataset.lectureId || element.getAttribute('data-lecture-id');
    const lectureType = element.dataset.lectureType || element.getAttribute('data-lecture-type') || 'FREE';

    if (!lectureId) {
        alert('강좌 정보를 확인할 수 없습니다.');
        return;
    }

    // 로그인 상태 확인 (필요시)
    if (typeof window.checkLoginStatus === 'function' && !window.checkLoginStatus()) {
        alert('로그인이 필요한 서비스입니다.');
        // 로그인 페이지로 리다이렉트 또는 로그인 모달 표시
        return;
    }

    // 수강 신청 모달 표시
    showEduApplicationModal(lectureId, lectureType);
}

// 전역 함수로 등록 - 함수명 충돌 방지
window.openEduModal = showEduApplicationModal; // 메인 진입점 (함수명 변경)
window.showEduApplicationModal = showEduApplicationModal; // 호환성 유지
window.closeEduApplicationModal = closeEduApplicationModal;
window.openEduTargetModal = openEduTargetModal;
window.handleEduApplicationClick = handleEduApplicationClick;

/**
 * 키보드 이벤트 처리 (ESC 키로 모달 닫기)
 */
document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
        const modalElement = document.getElementById('eduApplicationModal');
        if (modalElement && modalElement.style.display !== 'none') {
            closeEduApplicationModal(event);
        }
    }
});

/**
 * 모달 배경 클릭 시 닫기 비활성화 - 사용자가 의도치 않게 모달을 닫는 것을 방지
 */
// 모달 배경 클릭으로 닫기 기능 제거됨

/**
 * 모듈 초기화 및 상태 확인
 */
document.addEventListener('DOMContentLoaded', () => {
    // Vue.js 사용 가능 여부 확인
    if (typeof Vue === 'undefined') {
        console.warn('Vue.js가 로드되지 않았습니다. 수강 신청 모달이 작동하지 않을 수 있습니다.');
    }

    // 기존 수강신청 버튼들에 자동으로 이벤트 핸들러 연결 (선택사항)
    const applicationButtons = document.querySelectorAll('[data-action="edu-application"]');
    applicationButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
            event.preventDefault();
            handleEduApplicationClick(event.target);
        });
    });
});

// CommonJS/AMD 호환성
if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        showEduApplicationModal,
        closeEduApplicationModal,
        openEduTargetModal,
        handleEduApplicationClick,
    };
}



// WEBPACK FOOTER //
// ./src/main/education/EduApplicationModalIntegration.js